/**
 * LiveMatchContextBanner — Dynamic real-time context metrics displayed
 * above the analytics view during basketball matches.
 *
 * Displays:
 *   - On-court average age per team (computed from active 5-player roster)
 *   - Effective time / Ineffective time totals
 */
import React, { useMemo } from "react";
import type { Match, Player } from "../../types";

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface LiveMatchContextBannerProps {
  match: Match;
  onCourtHomePlayers: Player[];
  onCourtAwayPlayers: Player[];
  effectiveTime: number;
  ineffectiveSeconds: number;
  t: (key: string, fallback?: string) => string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function parseAge(birthDateStr?: string | null): number | null {
  if (!birthDateStr) return null;
  const d = new Date(birthDateStr);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age > 0 && age < 100 ? age : null;
}

function averageAge(players: Player[]): string {
  const ages = players.map((p) => parseAge(p.birth_date)).filter((a): a is number => a !== null);
  if (ages.length === 0) return "—";
  return (ages.reduce((s, a) => s + a, 0) / ages.length).toFixed(1);
}

function formatSeconds(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const LiveMatchContextBanner: React.FC<LiveMatchContextBannerProps> = ({
  match,
  onCourtHomePlayers,
  onCourtAwayPlayers,
  effectiveTime,
  ineffectiveSeconds,
  t,
}) => {
  const homeAvgAge = useMemo(
    () => averageAge(onCourtHomePlayers),
    [onCourtHomePlayers],
  );
  const awayAvgAge = useMemo(
    () => averageAge(onCourtAwayPlayers),
    [onCourtAwayPlayers],
  );

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-900/80 border border-slate-700 px-4 py-2"
      data-testid="live-match-context"
    >
      {/* Effective time */}
      <div className="flex items-center gap-2 text-xs text-slate-300">
        <span className="text-slate-500 uppercase tracking-wide">
          {t("effectiveTime", "Effective")}
        </span>
        <span
          className="font-mono font-semibold text-emerald-400"
          data-testid="stat-total-effective-time"
        >
          {formatSeconds(effectiveTime)}
        </span>
      </div>

      {/* Ineffective time */}
      <div className="flex items-center gap-2 text-xs text-slate-300">
        <span className="text-slate-500 uppercase tracking-wide">
          {t("ineffectiveTime", "Ineffective")}
        </span>
        <span
          className="font-mono font-semibold text-amber-400"
          data-testid="stat-total-ineffective-time"
        >
          {formatSeconds(ineffectiveSeconds)}
        </span>
      </div>

      {/* On-court avg age - Home */}
      <div className="flex items-center gap-2 text-xs text-slate-300">
        <span className="text-emerald-500 font-medium truncate max-w-[80px]">
          {match.home_team.short_name}
        </span>
        <span className="text-slate-500 uppercase tracking-wide">
          {t("liveMatchContext.onCourtAvgAge", "Avg Age")}
        </span>
        <span
          className="font-mono font-semibold"
          data-testid="stat-on-field-age-home"
        >
          {homeAvgAge}
        </span>
      </div>

      {/* On-court avg age - Away */}
      <div className="flex items-center gap-2 text-xs text-slate-300">
        <span className="text-indigo-400 font-medium truncate max-w-[80px]">
          {match.away_team.short_name}
        </span>
        <span className="text-slate-500 uppercase tracking-wide">
          {t("liveMatchContext.onCourtAvgAge", "Avg Age")}
        </span>
        <span
          className="font-mono font-semibold"
          data-testid="stat-on-field-age-away"
        >
          {awayAvgAge}
        </span>
      </div>
    </div>
  );
};

export default LiveMatchContextBanner;
